import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { HotkeysTarget2 } from '@blueprintjs/core';
import classNames from 'classnames';
import { Button } from 'core/components';
import { HotkeyGroup } from 'app/util/constants/hotkeys';
import { DURATION, panelTransition } from 'core/styles/motion';

const DEFAULT_TRANSITION_MS = 150;
const ANIMATED_EXIT_MS = parseInt(DURATION.base, 10);

const StyledMenuWrapper = styled.div`
  position: fixed;
  ${({ topOffset, extraOffset = 0 }) => css`
    top: ${topOffset + extraOffset}px;
    height: calc(100vh - ${topOffset + extraOffset}px);
    max-height: calc(100vh - ${topOffset + extraOffset}px);
  `}
  right: -15px;
  background: ${({ theme }) => theme.colors.drawerBackground};

  box-shadow: ${(props) => props.theme.shadows.hovered};
  border-left: none;
  z-index: 7;
  opacity: 0;
  pointer-events: none;
  overflow-y: auto;
  overflow-x: hidden;

  ${({ $animated }) =>
    $animated
      ? panelTransition({ properties: ['right', 'left'] })
      : css`
          transition:
            opacity linear ${DEFAULT_TRANSITION_MS}ms,
            right linear ${DEFAULT_TRANSITION_MS}ms,
            left linear ${DEFAULT_TRANSITION_MS}ms;
        `}

  &.expanded {
    right: 0;
    opacity: 1;
    pointer-events: all;

    &.showDockedInLargeFullScreen {
      @media (min-width: 1600px) {
        display: block;
        position: relative;
        top: 0;
        right: unset;
        box-shadow: none;
        border-left: ${(props) => props.theme.borders.thin};
      }
    }

    &.forceDocked {
      display: block;
      position: relative;
      top: 0;
      right: unset;
      box-shadow: none;
      border-left: ${(props) => props.theme.borders.thin};
    }
  }

  &.left {
    right: unset;
    left: -15px;

    &.expanded {
      left: 0;

      &.showDockedInLargeFullScreen {
        @media (min-width: 1600px) {
          left: unset;
          border-right: ${(props) => props.theme.borders.thin};
          border-left-width: 0;
        }
      }

      &.forceDocked {
        left: unset;
        border-right: ${(props) => props.theme.borders.thin};
        border-left-width: 0;
      }
    }
  }
`;

class Drawer extends Component {
  static getDerivedStateFromProps({ contents, isOpen, animated }) {
    if (!animated) {
      return null;
    }

    if (contents) {
      return { renderedContents: contents };
    }

    return isOpen ? { renderedContents: null } : null;
  }

  state = { renderedContents: null };

  componentDidMount() {
    const { isOpen } = this.props;
    if (isOpen) {
      setTimeout(() => {
        window.addEventListener('click', this.handlePageClick);
      }, 250);
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen, animated } = this.props;
    if (isOpen && !prevProps.isOpen) {
      setTimeout(() => {
        window.addEventListener('click', this.handlePageClick);
      }, 250);
    } else if (!isOpen && prevProps.isOpen) {
      window.removeEventListener('click', this.handlePageClick);

      if (animated) {
        this.releaseContentsAfterTransition();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handlePageClick);
    clearTimeout(this.releaseContentsTimer);
  }

  releaseContentsAfterTransition() {
    clearTimeout(this.releaseContentsTimer);
    this.releaseContentsTimer = setTimeout(() => {
      const { isOpen } = this.props;

      if (!isOpen) {
        this.setState({ renderedContents: null });
      }
    }, ANIMATED_EXIT_MS);
  }

  handlePageClick = (e) => {
    const { isOpen, onClose, forceDocked, showDockedInLargeFullScreen } = this.props;
    if (isOpen) {
      const pageContentEl = document.querySelector('.page-main-content');
      const pageDrawerEl = document.querySelector('.page-drawer');

      const { matches } = window.matchMedia('(min-width: 1600px)');

      if (
        !!onClose &&
        !forceDocked &&
        (!showDockedInLargeFullScreen || !matches) && // if not docked
        pageContentEl &&
        pageDrawerEl &&
        pageContentEl.contains(e.target) &&
        !pageDrawerEl.contains(e.target)
      ) {
        onClose();
      }
    }
  };

  get hotkeys() {
    const { onClose, isOpen } = this.props;
    if (!isOpen || !onClose) {
      return [];
    }
    return [
      {
        global: true,
        combo: 'esc',
        group: HotkeyGroup.Drawer,
        label: 'Close Drawer',
        onKeyDown: () => onClose()
      }
    ];
  }

  render() {
    const {
      isOpen,
      contents,
      forceDocked,
      left,
      showDockedInLargeFullScreen,
      onClose,
      animated = false,
      ...drawerProps
    } = this.props;
    const { renderedContents } = this.state;
    const className = classNames('page-drawer', { expanded: isOpen, forceDocked, left, showDockedInLargeFullScreen });

    const { showCloseButton = false, ...restOfDrawerProps } = drawerProps;

    return (
      <HotkeysTarget2 hotkeys={this.hotkeys}>
        <StyledMenuWrapper
          key="menu"
          className={className}
          {...restOfDrawerProps}
          $animated={animated}
          role="dialog"
          tabIndex={0}
        >
          {showCloseButton && (
            <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1 }}>
              <Button minimal icon="cross" onClick={onClose} />
            </div>
          )}
          {animated ? renderedContents : contents}
        </StyledMenuWrapper>
      </HotkeysTarget2>
    );
  }
}

export default Drawer;
